// src/pages/Projects.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Archive, Loader,
  Activity, Brain, GitBranch, Calendar
} from 'lucide-react';
import api from '../services/api';
import EditProjectModal from '../components/EditProjectModal';

const Projects = () => {
  const [projects, setProjects]           = useState([]);
  const [stats, setStats]                 = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [projectName, setProjectName]         = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isLoading, setIsLoading]         = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      if (!projectName.trim()) {
        // You might want to show an error message to the user
        return;
      }
  
      const response = await api.post('/projects', {
        name: projectName.trim(),
        description: projectDescription.trim(),
      });
  
      // Add the new project to the list
      setProjects([...projects, response.data]);
      
      // Reset form and close modal
      setProjectName('');
      setProjectDescription('');
      setShowNewProjectModal(false);
  
      // Optionally refresh all projects to get latest stats
      fetchProjects();
  
    } catch (error) {
      console.error('Error creating project:', error);
      // You might want to show an error message to the user
    }
  };
  

  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      const [projectsRes, statsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/stats/me'),
      ]);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, description, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-gray-600 text-xs font-medium mb-0.5">{title}</div>
          <div className="text-xl font-bold mb-0.5">{value}</div>
          <div className="text-gray-500 text-xs">{description}</div>
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  const ProjectCard = ({ project, onEdit }) => (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-gray-900">{project.name}</h3>
          <Edit2
            size={14}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onEdit(project); }}
          />
        </div>
        
        <p className="text-gray-600 text-xs mb-3 line-clamp-2">
          {project.description || 'No description provided'}
        </p>

        <div className="flex items-center space-x-3 text-xs text-gray-500">
          <div className="flex items-center">
            <GitBranch size={12} className="mr-1" />
            {project.pipelines?.length || 0} pipelines
          </div>
          <div className="flex items-center">
            <Calendar size={12} className="mr-1" />
            {new Date(project.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>
      
      <div className="px-4 py-3 bg-gray-50 border-t flex justify-between items-center">
        <button
          onClick={() => navigate(`/project/${project._id}`)}
          className="text-blue-600 hover:text-blue-700 font-medium text-xs"
        >
          View Pipelines
        </button>
        <div className="flex -space-x-1.5">
          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
            {project.owner?.name?.[0] || 'U'}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-sm text-gray-600 mt-1">Manage and monitor your machine learning projects</p>
          </div>
          <button
            onClick={() => setShowNewProjectModal(true)}
            className="flex items-center space-x-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={16} />
            <span>Create Project</span>
          </button>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Archive}
            title="Total Projects"
            value={projects.length}
            description="Active projects"
            color="bg-blue-500"
          />
          <StatCard
            icon={GitBranch}
            title="Total Pipelines"
            value={stats?.totalPipelines ?? '—'}
            description="Across all projects"
            color="bg-purple-500"
          />
          <StatCard
            icon={Brain}
            title="Trained Models"
            value={stats?.totalModels ?? '—'}
            description="Saved to your library"
            color="bg-indigo-500"
          />
          <StatCard
            icon={Activity}
            title="Avg Accuracy / R²"
            value={stats?.avgAccuracy != null ? `${(stats.avgAccuracy * 100).toFixed(1)}%` : '—'}
            description="Across completed models"
            color="bg-orange-500"
          />
        </div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-48">
            <Loader className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <ProjectCard key={project._id} project={project} onEdit={setEditingProject} />
            ))}
          </div>
        )}

        {editingProject && (
          <EditProjectModal
            project={editingProject}
            onClose={() => setEditingProject(null)}
            onSave={(updated) => {
              setProjects(projects.map(p => p._id === updated._id ? updated : p));
              setEditingProject(null);
            }}
          />
        )}

        {/* New Project Modal */}
        {showNewProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Create New Project</h2>
              <form onSubmit={handleCreateProject}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Project Name
                    </label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Enter project name"
                      className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Enter project description"
                      rows={3}
                      className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowNewProjectModal(false)}
                    className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Create Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );

};

export default Projects;